<template>
  <a-card title="Whitelisted domains" size="small" class="whitelist-card">
    <p class="panel-help mb-3">
      Trusted hosts for hidden-links checks. Add domains one by one, or paste a list when needed.
      <a-tooltip>
        <template slot="title">
          Enter the host only, e.g. <strong>cloudsine.tech</strong> &mdash; not
          <strong>www.cloudsine.tech</strong>. The <code>www.</code> prefix is a subdomain,
          not the host, so including it will not match.
        </template>
        <a-icon type="info-circle" class="panel-help__info" />
      </a-tooltip>
    </p>
    <p class="panel-help panel-help--hint mb-3">
      Tip: enter the host without the <code>www.</code> prefix (use
      <strong>cloudsine.tech</strong>, not <strong>www.cloudsine.tech</strong>).
    </p>
    <a-checkbox v-model="applyToAllDomains" class="apply-all-checkbox mb-3">
      Apply to all domains
    </a-checkbox>

    <div class="whitelist-workspace">
      <div class="whitelist-pane whitelist-pane--add">
        <div class="whitelist-pane__title">
          {{ applyToAllDomains ? 'Add domains for all domains' : 'Domains to add' }}
        </div>
        <div class="text-muted fs-12 mb-2">
          One-per-line or comma-separated.
        </div>
        <div class="whitelist-pane__body">
        <a-textarea
          v-model="bulkInput"
          :rows="10"
          class="bulk-paste-panel__textarea"
          placeholder="example.com
cdn.example.com
or comma separated"
        />
        </div>
        <div class="mt-2">
          <a-button
            type="primary"
            :disabled="bulkInputDomains.length === 0 || saving"
            :loading="saving"
            @click="onAddBulkInput"
          >
            Add domains
          </a-button>
        </div>
      </div>

      <div class="whitelist-pane whitelist-pane--remove">
        <div class="whitelist-pane__title">
          {{ applyToAllDomains ? 'Remove domains for all domains' : 'Current whitelisted domains' }}
        </div>
        <div class="text-muted fs-12 mb-2">
          One-per-line or comma-separated.
        </div>
        <div class="whitelist-pane__body">
          <a-textarea
            v-model="bulkRemoveInput"
            :rows="10"
            class="bulk-paste-panel__textarea"
            placeholder="example.com
cdn.example.com
or comma separated"
          />
        </div>
        <div class="mt-2 middle-actions-row">
          <a-button
            type="primary"
            :disabled="bulkRemoveDomains.length === 0 || saving"
            :loading="saving"
            @click="onRemoveBulkInput"
          >
            Remove domains
          </a-button>
        </div>
      </div>

      <div class="whitelist-pane whitelist-pane--current">
        <div class="whitelist-pane__title">Current domains list</div>
        <a-input
          v-model="domainListQuery"
          size="small"
          class="current-domain-search"
          placeholder="Search domains..."
        />
        <div class="text-muted fs-12 mt-2 mb-2">
          {{ activeDomains.length }} total domain(s)
        </div>
        <div class="whitelist-pane__body">
          <div v-if="filteredActiveDomains.length === 0" class="text-muted fs-12 mt-1">
            No domains match your search.
          </div>
          <div v-else class="current-domain-list">
            <div
              v-for="(domain, index) in filteredActiveDomains"
              :key="`current-${domain}`"
              class="current-domain-list__item"
            >
              <span class="current-domain-list__index">{{ index + 1 }}.</span>
              <span class="current-domain-list__text">{{ domain }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="lastInvalidDomains.length" class="invalid-list mt-2">
      <strong>Invalid:</strong> {{ lastInvalidDomains.join(', ') }}
    </div>
  </a-card>
</template>

<script>
import {
  isValidDomain as isValidDomainUtil,
  normalizeDomainList as normalizeDomainListUtil,
} from '../utils/domain.utils';

export default {
  name: 'AiTriageWhitelistedDomainsPanel',
  props: {
    domains: {
      type: Array,
      default: () => [],
    },
    allDomains: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      bulkInput: '',
      bulkRemoveInput: '',
      selectedDomains: [],
      allDomainsDraft: [],
      lastInvalidDomains: [],
      applyToAllDomains: false,
      domainListQuery: '',
    };
  },
  computed: {
    bulkInputDomains() {
      return this.parseDomainsText(this.bulkInput);
    },
    bulkRemoveDomains() {
      return this.parseDomainsText(this.bulkRemoveInput);
    },
    activeDomains() {
      return this.applyToAllDomains ? this.allDomainsDraft : this.selectedDomains;
    },
    filteredActiveDomains() {
      const q = String(this.domainListQuery || '').trim().toLowerCase();
      if (!q) {
        return this.activeDomains;
      }
      return this.activeDomains.filter((d) => d.includes(q));
    },
  },
  watch: {
    domains: {
      immediate: true,
      deep: true,
      handler(next) {
        const arr = Array.isArray(next) ? next : [];
        const normalized = this.normalizeDomains(arr);
        this.selectedDomains = normalized;
      },
    },
    allDomains: {
      immediate: true,
      deep: true,
      handler(next) {
        const arr = Array.isArray(next) ? next : [];
        this.allDomainsDraft = this.normalizeDomains(arr);
      },
    },
  },
  methods: {
    parseDomainsText(raw) {
      return String(raw || '')
        .split(/[\n,]/g)
        .map((v) => v.trim().toLowerCase())
        .filter(Boolean);
    },
    normalizeDomains(arr) {
      return normalizeDomainListUtil(arr);
    },
    isValidDomain(value) {
      return isValidDomainUtil(value);
    },
    addDomains(parsedDomains) {
      const invalid = parsedDomains.filter((d) => !this.isValidDomain(d));
      const valid = parsedDomains.filter((d) => this.isValidDomain(d));
      this.lastInvalidDomains = Array.from(new Set(invalid));
      if (valid.length === 0) {
        return this.activeDomains;
      }
      const merged = this.normalizeDomains([...this.activeDomains, ...valid]);
      if (this.applyToAllDomains) {
        this.allDomainsDraft = merged;
      } else {
        this.selectedDomains = merged;
      }
      return merged;
    },
    onAddBulkInput() {
      const inputDomains = this.normalizeDomains(this.bulkInputDomains);
      const nextDomains = this.addDomains(this.bulkInputDomains);
      this.bulkInput = '';
      // Add button persists additions immediately (add-only path).
      this.$emit('save-whitelisted-domains', {
        domains: nextDomains,
        deltaDomains: inputDomains,
        mode: 'add-only',
        applyToAllDomains: this.applyToAllDomains,
      });
    },
    onRemoveBulkInput() {
      const removeSet = new Set(this.normalizeDomains(this.bulkRemoveDomains));
      const removedDomains = this.activeDomains.filter((d) => removeSet.has(d));
      const nextDomains = this.activeDomains.filter((d) => !removeSet.has(d));
      if (this.applyToAllDomains) {
        this.allDomainsDraft = nextDomains;
      } else {
        this.selectedDomains = nextDomains;
      }
      this.bulkRemoveInput = '';
      // Remove button persists removals immediately (remove-only path).
      this.$emit('save-whitelisted-domains', {
        domains: nextDomains,
        removedDomains,
        mode: 'remove-only',
        applyToAllDomains: this.applyToAllDomains,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.whitelist-card {
  min-height: calc(100vh - 290px);
}

.whitelist-card ::v-deep .ant-card-body {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 360px);
}

.panel-help {
  font-size: 13px;
  color: #595959;
  line-height: 1.5;
}

.panel-help__info {
  margin-left: 4px;
  color: #1890ff;
  cursor: help;
}

.panel-help--hint {
  margin-top: -8px;
  font-size: 12px;
  color: #8c8c8c;
}

.panel-help--hint code {
  background: #f5f5f5;
  border-radius: 3px;
  padding: 0 4px;
}

.apply-all-checkbox {
  font-size: 13px;
}

.whitelist-workspace {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(260px, 1fr) minmax(260px, 1fr);
  gap: 14px;
  align-items: stretch;
  min-height: 360px;
}

.whitelist-pane {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 12px;
  background: #fff;
  min-height: 360px;
  display: flex;
  flex-direction: column;
}

.whitelist-pane__body {
  flex: 1 1 auto;
  min-height: 0;
}

.whitelist-pane__title {
  font-size: 12px;
  font-weight: 600;
  color: #8c8c8c;
  margin-bottom: 8px;
}

.bulk-paste-panel__textarea {
  width: 100%;
  height: 100%;
  min-height: 260px;
}

.current-domain-search {
  width: 100%;
}

.current-domain-list {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  max-height: 320px;
  overflow: auto;
}

.current-domain-list__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid #f5f5f5;
  background: #fff;
}

.current-domain-list__item:last-child {
  border-bottom: 0;
}

.current-domain-list__item:nth-child(even) {
  background: #fafafa;
}

.current-domain-list__index {
  color: #8c8c8c;
  font-size: 12px;
  min-width: 22px;
}

.current-domain-list__text {
  word-break: break-all;
  color: #595959;
  font-size: 13px;
}

.invalid-list {
  color: #d4380d;
  font-size: 12px;
}

.middle-actions-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.text-muted {
  color: #888;
}

.fs-12 {
  font-size: 12px;
}

@media (max-width: 860px) {
  .whitelist-workspace {
    grid-template-columns: 1fr;
  }
}
</style>
